import { inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { Observable, tap } from 'rxjs';

import {
  AppointmentOptionType,
  isAppointmentOptionType,
} from '../components/calendar/appointment-option.interface';
import { API_ROUTES } from '../consts/api-routes.const';
import { HttpService } from './http.service';

/**
 * Backend booking-state (GET/PATCH /wp/planner/state), keyed on the x-license-plate header.
 * Every field stays null until its step is done. `jobs` is a fieldId → (true | number) map,
 * the same shape as the submission payload.
 */
export interface BookingState {
  jobs: Record<string, boolean | number> | null;
  licensePlate: string | null;
  mileage: number | null;
  establishment: number | null;
  willBeWaiting: boolean | null;
  date: string | null;
  time: string | null;
  replacementVehicle: number | null;
  // Read-only: derived from the selected jobs, recalculated on every job submit. Never patchable.
  waitingTime?: number | null;
  // Free-form key→scalar scratch map (manufacturer/model among others); GET always returns it.
  extra?: Record<string, string | number | boolean>;
}

// In a PATCH a null value removes that one key from the stored extra map.
export type BookingStateExtraPatch = Record<string, string | number | boolean | null>;

type BookingStateMutable = Pick<
  BookingState,
  'establishment' | 'willBeWaiting' | 'date' | 'time' | 'replacementVehicle'
>;

// jobs + mileage go through POST /wp/planner/jobs; licensePlate comes from the header — those
// three are not patchable (the backend rejects mileage in PATCH with 422 "Unknown field").
export type BookingStatePatch = Partial<BookingStateMutable> & { extra?: BookingStateExtraPatch };

// The extra keys step 3 owns. Named so page and restore cannot drift apart.
export const APPOINTMENT_OPTION_KEY = 'appointmentOption';
export const REPLACEMENT_TRANSPORT_KEY = 'replacementTransport';

// The chosen appointment option travels as a free-form extra key (so as a string): narrow it back
// to the union. `willBeWaiting` is a bool, so it cannot tell dropOff from collectDeliver — hence
// the extra key. Without a stored date there is no valid choice, and a lone flag may not
// half-restore the step.
export function appointmentOptionFromState(
  state: BookingState | null,
): AppointmentOptionType | null {
  if (state?.date == null) {
    return null;
  }
  const raw: string | number | boolean | undefined = state.extra?.[APPOINTMENT_OPTION_KEY];
  if (isAppointmentOptionType(raw)) {
    return raw;
  }
  // States written before the option types existed: the waiting flag was all there was, and
  // collect & deliver did not exist yet, so a non-waiting appointment was always a drop-off.
  if (state.willBeWaiting === true) {
    return 'wait';
  }
  return state.willBeWaiting === false ? 'dropOff' : null;
}

@Injectable({ providedIn: 'root' })
export class BookingStateService {
  private readonly httpService: HttpService = inject(HttpService);

  private readonly _state: WritableSignal<BookingState | null> = signal<BookingState | null>(null);
  public readonly state: Signal<BookingState | null> = this._state.asReadonly();

  // GET the full state (resume source) and cache it in the signal.
  public load(): Observable<BookingState> {
    return this.peek().pipe(tap((state: BookingState): void => this._state.set(state)));
  }

  // GET without caching the signal — for checks outside the booking flow (VehicleExtraService);
  // caching there would leak old-plate state into e.g. the mileage prefill.
  public peek(): Observable<BookingState> {
    return this.httpService.get<BookingState>(API_ROUTES.PLANNER.state());
  }

  // PATCH ALWAYS sends the full mutable state (see toFullState); the backend read-modify-writes
  // the whole state, so a reordered PATCH can't clobber another field. We cache the response.
  public patch(partial: BookingStatePatch): Observable<BookingState> {
    // `extra` merges server-side (absent = keep everything), so it stays out of toFullState;
    // only explicitly passed keys are sent.
    const body: BookingStatePatch = {
      ...this.toFullState(partial),
      ...(partial.extra !== undefined ? { extra: partial.extra } : {}),
    };
    return this.httpService
      .patch<BookingState>(API_ROUTES.PLANNER.state(), body)
      .pipe(tap((state: BookingState): void => this._state.set(state)));
  }

  // Tops the partial up to the full set of mutable fields from the cached state. `in` checks keep
  // an explicit null (field deliberately empty) apart from absent (take the cached value).
  private toFullState(partial: BookingStatePatch): BookingStateMutable {
    const c: BookingState | null = this._state();
    return {
      establishment:
        'establishment' in partial ? (partial.establishment ?? null) : (c?.establishment ?? null),
      willBeWaiting:
        'willBeWaiting' in partial ? (partial.willBeWaiting ?? null) : (c?.willBeWaiting ?? null),
      date: 'date' in partial ? (partial.date ?? null) : (c?.date ?? null),
      time: 'time' in partial ? (partial.time ?? null) : (c?.time ?? null),
      replacementVehicle:
        'replacementVehicle' in partial
          ? (partial.replacementVehicle ?? null)
          : (c?.replacementVehicle ?? null),
    };
  }

  public clear(): void {
    this._state.set(null);
  }
}
